import { formatTime } from "../../utils/formatters";
import { parseUtcTimestamp } from "../../services/dateTime";
import { batchApi } from "../../api/batchApi";
import { analyticsScopeFilters, hasAnalyticsTimeFilter, matchesAnalyticsBatch, matchesAnalyticsTime, matchesMachine } from "../../components/analytics/analyticsFilters";

const numeric = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
export const PARAMETER_DEFINITIONS = [
  { key: "temperature", label: "Temperature", unit: "°C" },
  { key: "current", label: "Current", unit: "A" },
  { key: "time", label: "Hold Time", unit: "s" },
];

export const selectCurrentBatch = (batches, filters = {}, plantId, machines = []) => {
  const scope = analyticsScopeFilters(filters);
  const eligibleMachines = new Set(machines.filter((machine) => matchesMachine(machine, scope, plantId)).map((machine) => machine.machine_id));
  // The active batch can start before the selected period. Its captured cycles
  // are filtered separately, so midnight does not make a running batch vanish.
  return batches.filter((batch) => ["WIP", "Hold", "Rework"].includes(batch.status) && matchesAnalyticsBatch(batch, scope, plantId) && (!filters.machineType || eligibleMachines.has(batch.raw?.machine_id)))
    .sort((first, second) => String(second.raw?.started_at || second.lastUpdated || "").localeCompare(String(first.raw?.started_at || first.lastUpdated || "")))[0] || null;
};

export const selectAnalyticsAlerts = (alerts, batches, filters = {}, plantId, machines = []) => {
  const scope = analyticsScopeFilters(filters);
  const eligibleMachines = new Set(machines.filter((machine) => matchesMachine(machine, scope, plantId)).map((machine) => machine.machine_id));
  const batchIds = new Set(batches.filter((batch) => matchesAnalyticsBatch(batch, scope, plantId)
    && (!filters.machineType || eligibleMachines.has(batch.raw?.machine_id))).map((batch) => batch.batchId));
  return alerts.filter((alert) => batchIds.has(alert.batchId) && matchesAnalyticsTime(alert.createdAt, filters));
};

export const telemetryFromCycles = (batch, response, filters = {}) => {
  const raw = batch?.raw || batch || {};
  const cycles = (response?.data || []).filter((cycle) => matchesAnalyticsTime(cycle.captured_at || cycle.created_at, filters)).sort((first, second) => String(first.captured_at || first.created_at || "").localeCompare(String(second.captured_at || second.created_at || "")) || Number(first.cycle_no) - Number(second.cycle_no));
  const statuses = cycles.map((cycle) => String(cycle.disposition || cycle.status || "").toUpperCase());
  const classified = statuses.every((status) => ["OK", "NG", "HOLD", "REWORK", "REJECT"].includes(status));
  const countStatus = (status) => classified ? statuses.filter((value) => value === status).length : null;
  const periodOnly = hasAnalyticsTimeFilter(filters);
  const totalCycle = periodOnly ? cycles.length : numeric(raw.total_cycles) ?? cycles.length;
  const completeCycleData = periodOnly || totalCycle === cycles.length;
  const inferredCount = (status) => completeCycleData ? countStatus(status) : null;
  const okParts = !periodOnly && numeric(raw.ok_parts) !== null ? numeric(raw.ok_parts) : inferredCount("OK");
  // Rework and reject are subsets of hold, matching the batch summary contract.
  const holdParts = !periodOnly && numeric(raw.hold_parts) !== null ? numeric(raw.hold_parts) : classified && completeCycleData ? cycles.length - countStatus("OK") : null;
  const rework = !periodOnly && numeric(raw.rework_parts) !== null ? numeric(raw.rework_parts) : inferredCount("REWORK");
  const reject = !periodOnly && numeric(raw.reject_parts) !== null ? numeric(raw.reject_parts) : inferredCount("REJECT");
  const parameters = PARAMETER_DEFINITIONS.map((definition) => {
    const samples = cycles.filter((cycle) => numeric(cycle[definition.key]) !== null);
    const latest = samples[samples.length - 1];
    const hasSnapshot = latest && Object.prototype.hasOwnProperty.call(latest, "limits");
    const limits = (hasSnapshot ? latest.limits?.[definition.key] : response?.meta_data?.limits?.[definition.key] || response?.limits?.[definition.key]) || {};
    const normalizedLimits = Object.fromEntries(["lsl", "lcl", "ucl", "usl"].map((key) => [key, numeric(limits[key] ?? limits[key.toUpperCase()])]));
    const thresholds = [["usl", "USL", "#f43f5e"], ["ucl", "UCL", "#eab308"], ["lcl", "LCL", "#eab308"], ["lsl", "LSL", "#f43f5e"]]
      .map(([key, label, color]) => ({ value: normalizedLimits[key], label, color }))
      .filter((threshold) => threshold.value !== null);
    const value = latest ? numeric(latest[definition.key]) : null;
    const lower = normalizedLimits.lsl;
    const upper = normalizedLimits.usl;
    const padding = lower !== null && upper !== null ? (upper - lower) * 0.1 : 0;
    return {
      ...definition, unit: limits.unit || definition.unit,
      value,
      gauge: latest && lower !== null && upper !== null && upper > lower ? {
        value, min: Math.min(lower - padding, value), max: Math.max(upper + padding, value),
        unit: limits.unit || definition.unit, limits: normalizedLimits,
      } : null,
      thresholds,
      categories: samples.map((cycle) => {
        const timestamp = parseUtcTimestamp(cycle.captured_at || cycle.created_at);
        return timestamp ? formatTime(timestamp, { second: "2-digit" }) : `Cycle ${cycle.cycle_no}`;
      }),
      data: samples.map((cycle) => numeric(cycle[definition.key])),
    };
  });
  return { parameters, summary: { totalCycle, okParts, holdParts, rework, reject, okRate: totalCycle > 0 && okParts !== null ? Number((okParts / totalCycle * 100).toFixed(1)) : null }, latestAt: cycles[cycles.length - 1]?.captured_at || null };
};

export const getLiveTelemetry = async (filters = {}, batch) => {
  if (!batch?.uid) return null;
  return telemetryFromCycles(batch, await batchApi.getAllCycles(batch.uid), filters);
};
